import { discountPercent, escapeHtml, safeUrl } from './format.js';
import { groupBySection } from './paginate.js';
import { featuredItem } from './templates/cafe-panels.js';
import { badge, description, listCss, listItem, priceBlock, type RenderContext } from './templates/common.js';
import type { MenuBoardContent, MenuItemContent } from './types.js';

/**
 * Custom designs: menus people lay out themselves on a canvas, element by element, and keep as
 * templates. A design is plain data, drawn by the same renderer on the dashboard and the screens.
 *
 * Menu content is never typed into a design. Lists, item cards and some texts are bound to
 * slots of whichever board uses the design ("section 1", "item 2 of section 1", "the featured
 * item", "the brand"), so one design serves many boards and a price change still reaches every
 * screen. Colours may name theme tokens (`theme:accent`), so a board's colours recolour it.
 */

export const DESIGN_SIZES = {
  landscape: { width: 1920, height: 1080 },
  portrait: { width: 1080, height: 1920 },
} as const;

export const THEME_COLOR_TOKENS = ['bg', 'surface', 'text', 'primary', 'accent'] as const;
export type ThemeColorToken = (typeof THEME_COLOR_TOKENS)[number];

/** A hex colour, or `theme:<token>` to follow the board's theme. */
export type DesignColor = string;

export type DesignFontRole = 'heading' | 'body' | 'script';

/** Which item of the board an element shows. Section and item are zero-based. */
export type ItemSlot = { kind: 'item'; section: number; item: number } | { kind: 'featured' };

export type TextBinding =
  | { kind: 'title' }
  | { kind: 'brand' }
  | { kind: 'tagline' }
  | { kind: 'section'; section: number };

interface DesignBase {
  id: string;
  /** Shown in the layers panel. */
  name?: string | null;
  x: number;
  y: number;
  w: number;
  h: number;
  rotation: number;
  opacity: number;
  locked?: boolean;
}

export interface DesignText extends DesignBase {
  type: 'text';
  text: string;
  textAr: string | null;
  bind: TextBinding | null;
  font: DesignFontRole;
  size: number;
  weight: number;
  color: DesignColor;
  align: 'left' | 'center' | 'right';
  italic: boolean;
  uppercase: boolean;
  letterSpacing: number;
  lineHeight: number;
}

export interface DesignImage extends DesignBase {
  type: 'image';
  /** A fixed picture from the media library, used when `source` is null. */
  mediaId: string | null;
  /** Or the photo of a board's item. */
  source: ItemSlot | null;
  fit: 'cover' | 'contain';
  /** Corner rounding as a percentage of the shorter side; 50 is a circle. */
  radius: number;
  borderWidth: number;
  borderColor: DesignColor;
  shadow: boolean;
}

export const DESIGN_SHAPES = ['rect', 'ellipse', 'pill', 'ribbon', 'burst', 'hexagon', 'tag'] as const;
export type DesignShapeKind = (typeof DESIGN_SHAPES)[number];

export interface DesignShape extends DesignBase {
  type: 'shape';
  shape: DesignShapeKind;
  fill: DesignColor;
  stroke: DesignColor | null;
  strokeWidth: number;
  radius: number;
  /** Optional label inside the shape, such as "50% OFF". */
  text: string | null;
  textColor: DesignColor;
  textSize: number;
  font: DesignFontRole;
}

export interface DesignLine extends DesignBase {
  type: 'line';
  color: DesignColor;
  thickness: number;
  style: 'solid' | 'dashed' | 'dotted';
}

export interface DesignMenuList extends DesignBase {
  type: 'menu-list';
  /** A board section by position, or null for every item. */
  section: number | null;
  showHeading: boolean;
  columns: number;
  font: DesignFontRole;
  nameSize: number;
  uppercase: boolean;
  headingColor: DesignColor;
  nameColor: DesignColor;
  priceColor: DesignColor;
  textColor: DesignColor;
  showDescriptions: boolean;
  leader: 'dotted' | 'dashed' | 'none';
}

export interface DesignItemCard extends DesignBase {
  type: 'item-card';
  source: ItemSlot;
  layout: 'vertical' | 'horizontal';
  showPhoto: boolean;
  photoShape: 'circle' | 'rounded' | 'square';
  showDescription: boolean;
  showPrice: boolean;
  font: DesignFontRole;
  nameSize: number;
  nameColor: DesignColor;
  priceColor: DesignColor;
  textColor: DesignColor;
  align: 'left' | 'center';
}

export type DesignElement = DesignText | DesignImage | DesignShape | DesignLine | DesignMenuList | DesignItemCard;
export type DesignElementType = DesignElement['type'];

export interface CustomDesign {
  width: number;
  height: number;
  background: { color: DesignColor; mediaId: string | null; overlay: number };
  elements: DesignElement[];
}

/* ------------------------------------------------------------------- resolving */

const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const TOKEN_VARS: Record<ThemeColorToken, string> = {
  bg: 'var(--mb-bg)',
  surface: 'var(--mb-surface)',
  text: 'var(--mb-text)',
  primary: 'var(--mb-primary)',
  accent: 'var(--mb-accent)',
};

/** A design colour as CSS: theme tokens become the board's variables; anything odd is dropped. */
export function designColor(value: DesignColor | null | undefined, fallback = 'transparent'): string {
  if (!value) return fallback;
  if (value.startsWith('theme:')) return TOKEN_VARS[value.slice(6) as ThemeColorToken] ?? fallback;
  return HEX.test(value) ? value : fallback;
}

export const FONT_VARS: Record<DesignFontRole, string> = {
  heading: 'var(--mb-font-heading)',
  body: 'var(--mb-font-body)',
  script: 'var(--mb-font-script)',
};

function num(value: unknown, fallback: number, min = -1e6, max = 1e6): number {
  const n = typeof value === 'number' && Number.isFinite(value) ? value : fallback;
  return Math.min(max, Math.max(min, n));
}

/** The item a slot points at on this board, or null when the board has no such item. */
export function resolveSlot(board: MenuBoardContent, slot: ItemSlot | null | undefined): MenuItemContent | null {
  if (!slot) return null;
  if (slot.kind === 'featured') return featuredItem(board);
  const group = groupBySection(board)[slot.section];
  return group?.items[slot.item] ?? null;
}

/** Every picture a design itself can show (bound item photos come with the board's items). */
export function designMediaIds(design: CustomDesign | null | undefined): string[] {
  if (!design) return [];
  const ids = [design.background?.mediaId];
  for (const el of design.elements ?? []) if (el.type === 'image' && !el.source) ids.push(el.mediaId);
  return ids.filter((id): id is string => Boolean(id));
}

/* -------------------------------------------------------------------- drawing */

const S = '.mb-t-custom';

function bilingualText(ctx: RenderContext, en: string | null | undefined, ar: string | null | undefined, cls: string): string {
  const e = en?.trim() || '';
  const a = ar?.trim() || '';
  if (ctx.showEn && ctx.showAr && e && a) {
    return `<span class="${cls}">${escapeHtml(e)}</span><span class="${cls} cu-ar2 mb-ar" dir="rtl" lang="ar">${escapeHtml(a)}</span>`;
  }
  if (!ctx.showEn && a) return `<span class="${cls} mb-ar" dir="rtl" lang="ar">${escapeHtml(a)}</span>`;
  return `<span class="${cls}">${escapeHtml(e || a)}</span>`;
}

function textFor(ctx: RenderContext, el: DesignText): [string | null, string | null] {
  const { board } = ctx;
  switch (el.bind?.kind) {
    case 'title':
      return [board.settings.title ?? board.name, board.settings.titleAr];
    case 'brand':
      return [board.brand.name, board.brand.nameAr];
    case 'tagline':
      return [board.brand.tagline, board.brand.taglineAr];
    case 'section': {
      const section = groupBySection(board)[el.bind.section]?.section;
      return [section?.title ?? null, section?.titleAr ?? null];
    }
    default:
      return [el.text, el.textAr];
  }
}

export const SHAPE_CLIP: Partial<Record<DesignShapeKind, string>> = {
  ribbon: 'polygon(0 0,100% 0,94% 50%,100% 100%,0 100%,6% 50%)',
  burst:
    'polygon(50% 0,61% 13%,77% 5%,80% 22%,97% 25%,89% 40%,100% 52%,87% 63%,93% 79%,76% 80%,70% 97%,56% 88%,42% 100%,34% 85%,18% 92%,17% 75%,1% 70%,10% 55%,0 41%,15% 32%,9% 16%,26% 15%,33% 0)',
  hexagon: 'polygon(6% 0,94% 0,100% 50%,94% 100%,6% 100%,0 50%)',
  tag: 'polygon(0 0,88% 0,100% 50%,88% 100%,0 100%)',
};

function element(ctx: RenderContext, el: DesignElement): string {
  const box = [
    `left:${num(el.x, 0)}em`,
    `top:${num(el.y, 0)}em`,
    `width:${num(el.w, 10, 1)}em`,
    `height:${num(el.h, 10, 1)}em`,
    `opacity:${num(el.opacity, 1, 0, 1)}`,
    el.rotation ? `transform:rotate(${num(el.rotation, 0, -360, 360)}deg)` : '',
  ];
  // The box sits on an outer element whose font size is the canvas unit, so `em` there means
  // canvas pixels; the inner element may set its own font size without moving the box.
  const wrap = (cls: string, style: string[], inner: string) =>
    `<div class="cu-el" data-el="${escapeHtml(el.id)}" style="${escapeHtml(box.filter(Boolean).join(';'))}">` +
    `<div class="cu-in ${cls}" style="${escapeHtml(style.filter(Boolean).join(';'))}">${inner}</div></div>`;

  switch (el.type) {
    case 'text': {
      const [en, ar] = textFor(ctx, el);
      return wrap(
        'cu-text',
        [
          `font-family:${FONT_VARS[el.font] ?? FONT_VARS.body}`,
          `font-size:${num(el.size, 40, 4, 600)}em`,
          `font-weight:${num(el.weight, 400, 100, 900)}`,
          `color:${designColor(el.color, 'var(--mb-text)')}`,
          `text-align:${['left', 'center', 'right'].includes(el.align) ? el.align : 'left'}`,
          `letter-spacing:${num(el.letterSpacing, 0, -1, 2)}em`,
          `line-height:${num(el.lineHeight, 1.15, 0.6, 3)}`,
          el.italic ? 'font-style:italic' : '',
          el.uppercase ? 'text-transform:uppercase' : '',
        ],
        bilingualText(ctx, en, ar, 'cu-t'),
      );
    }
    case 'image': {
      const item = el.source ? resolveSlot(ctx.board, el.source) : null;
      const url = safeUrl(ctx.img(el.source ? item?.imageMediaId : el.mediaId));
      const radius = `${num(el.radius, 0, 0, 50)}%`;
      const inner = url
        ? `<img class="cu-img" src="${escapeHtml(url)}" alt="" style="object-fit:${el.fit === 'contain' ? 'contain' : 'cover'};border-radius:${radius}">`
        : `<div class="cu-img cu-img-empty" style="border-radius:${radius}"></div>`;
      return wrap(
        'cu-image',
        [
          `border-radius:${radius}`,
          el.borderWidth ? `box-shadow:0 0 0 ${num(el.borderWidth, 0, 0, 100)}em ${designColor(el.borderColor, '#fff')}${el.shadow ? ',0 1.2em 2.4em rgba(0,0,0,.45)' : ''}` : el.shadow ? 'box-shadow:0 1.2em 2.4em rgba(0,0,0,.45)' : '',
        ],
        inner,
      );
    }
    case 'shape': {
      const clip = SHAPE_CLIP[el.shape];
      const radius =
        el.shape === 'ellipse' ? '50%' : el.shape === 'pill' ? '999em' : el.shape === 'rect' ? `${num(el.radius, 0, 0, 500)}em` : '0';
      const label = el.text?.trim()
        ? `<span class="cu-shape-label" style="${escapeHtml(`color:${designColor(el.textColor, '#fff')};font-size:${num(el.textSize, 32, 4, 400)}em;font-family:${FONT_VARS[el.font] ?? FONT_VARS.heading}`)}">${escapeHtml(el.text.trim())}</span>`
        : '';
      return wrap(
        'cu-shape',
        [
          `background:${designColor(el.fill)}`,
          `border-radius:${radius}`,
          clip ? `clip-path:${clip}` : '',
          el.stroke && el.strokeWidth && !clip ? `border:${num(el.strokeWidth, 0, 0, 100)}em solid ${designColor(el.stroke)}` : '',
        ],
        label,
      );
    }
    case 'line': {
      const style = ['solid', 'dashed', 'dotted'].includes(el.style) ? el.style : 'solid';
      return wrap('cu-rule', [], `<span style="${escapeHtml(`border-top:${num(el.thickness, 4, 1, 100)}em ${style} ${designColor(el.color, 'var(--mb-accent)')}`)}"></span>`);
    }
    case 'menu-list': {
      const groups = groupBySection(ctx.board);
      const group = el.section === null ? null : groups[el.section];
      const items = el.section === null ? groups.flatMap((g) => g.items) : (group?.items ?? []);
      const heading =
        el.showHeading && group
          ? `<div class="cu-list-head">${bilingualText(ctx, group.section.title, group.section.titleAr, 'cu-list-title')}</div>`
          : '';
      const body = items.length
        ? items.map((i) => listItem(ctx, i, 'cu', el.leader !== 'none')).join('')
        : '<p class="cu-list-empty">Section has no items on this board</p>';
      return wrap(
        `cu-list cu-leader-${el.leader}${el.uppercase ? ' cu-upper' : ''}${el.showDescriptions ? '' : ' cu-nodesc'}`,
        [
          `--cu-name:${designColor(el.nameColor, 'var(--mb-primary)')}`,
          `--cu-price:${designColor(el.priceColor, 'var(--mb-accent)')}`,
          `--cu-text:${designColor(el.textColor, 'var(--mb-text)')}`,
          `--cu-head:${designColor(el.headingColor, 'var(--mb-accent)')}`,
          `--cu-font:${FONT_VARS[el.font] ?? FONT_VARS.heading}`,
          `font-size:${num(el.nameSize, 30, 6, 200)}em`,
        ],
        `${heading}<div class="cu-list-body" style="--cu-cols:${Math.round(num(el.columns, 1, 1, 4))}">${body}</div>`,
      );
    }
    case 'item-card': {
      const item = resolveSlot(ctx.board, el.source);
      if (!item) return wrap('cu-card cu-card-missing', [], '<span>No item in this slot</span>');
      const photoUrl = el.showPhoto ? safeUrl(ctx.img(item.imageMediaId)) : null;
      const percent = discountPercent(item);
      const badges = badge(ctx, item, 'cu-badge');
      return wrap(
        `cu-card cu-card-${el.layout === 'horizontal' ? 'h' : 'v'} cu-photo-${el.photoShape}`,
        [
          `--cu-name:${designColor(el.nameColor, 'var(--mb-primary)')}`,
          `--cu-price:${designColor(el.priceColor, 'var(--mb-accent)')}`,
          `--cu-text:${designColor(el.textColor, 'var(--mb-text)')}`,
          `--cu-font:${FONT_VARS[el.font] ?? FONT_VARS.heading}`,
          `font-size:${num(el.nameSize, 34, 6, 200)}em`,
          `text-align:${el.align === 'center' ? 'center' : 'start'}`,
        ],
        [
          photoUrl ? `<div class="cu-card-photo"><img src="${escapeHtml(photoUrl)}" alt="">${percent !== null ? `<span class="cu-card-save" dir="ltr">-${percent}%</span>` : ''}</div>` : '',
          '<div class="cu-card-text">',
          bilingualText(ctx, item.title, item.titleAr, 'cu-card-name'),
          el.showDescription ? description(ctx, item, 'cu-card-desc') : '',
          badges && !photoUrl ? `<div class="cu-card-badges">${badges}</div>` : '',
          el.showPrice ? `<div class="cu-card-price">${priceBlock(ctx, item, 'cu')}</div>` : '',
          '</div>',
        ].join(''),
      );
    }
    default:
      return '';
  }
}

/** The canvas of a custom design: its background and elements, positioned in `em` = canvas px. */
export function renderCustomCanvas(ctx: RenderContext, design: CustomDesign): string {
  const bg = design.background ?? { color: 'theme:bg', mediaId: null, overlay: 0 };
  const photo = bg.mediaId ? safeUrl(ctx.img(bg.mediaId)) : null;
  const overlay = num(bg.overlay, 0, 0, 1);
  return [
    `<div class="cu-canvas" style="${escapeHtml(`width:${num(design.width, 1920, 100, 8000)}em;height:${num(design.height, 1080, 100, 8000)}em;background:${designColor(bg.color, 'var(--mb-bg)')}`)}">`,
    photo ? `<img class="cu-bg" src="${escapeHtml(photo)}" alt="">` : '',
    overlay ? `<div class="cu-bg-shade" style="opacity:${overlay}"></div>` : '',
    (design.elements ?? []).map((el) => element(ctx, el)).join(''),
    '</div>',
  ].join('');
}

export const CUSTOM_CSS = `
${S}{padding:0;align-items:center;justify-content:center;overflow:hidden}
${S} .cu-canvas{position:relative;flex:none;overflow:hidden}
${S} .cu-bg{position:absolute;inset:0;width:100%;height:100%;object-fit:cover}
${S} .cu-bg-shade{position:absolute;inset:0;background:#000}
${S} .cu-el{position:absolute;box-sizing:border-box;transform-origin:50% 50%}
${S} .cu-in{position:absolute;inset:0;box-sizing:border-box}
${S} .cu-text{display:flex;flex-direction:column;justify-content:center;white-space:pre-wrap;overflow-wrap:anywhere}
${S} .cu-text .cu-ar2{font-size:.62em;margin-top:.15em}
${S} .cu-text .mb-ar{line-height:1.35}
${S} .cu-image{overflow:visible}
${S} .cu-img{display:block;width:100%;height:100%}
${S} .cu-img-empty{background:repeating-linear-gradient(45deg,rgba(128,128,128,.25) 0 .8em,rgba(128,128,128,.12) .8em 1.6em)}
${S} .cu-shape{display:flex;align-items:center;justify-content:center;overflow:hidden}
${S} .cu-shape-label{font-weight:800;line-height:1;text-align:center;white-space:pre-wrap;padding:0 .3em}
${S} .cu-rule{display:flex;align-items:center}
${S} .cu-rule span{display:block;width:100%}
${S} .cu-list{overflow:hidden;color:var(--cu-text);font-size:30em;display:flex;flex-direction:column}
${S} .cu-list-head{flex:none;margin-bottom:.5em}
${S} .cu-list-title{display:block;font-family:var(--cu-font);font-weight:var(--mb-heading-weight);color:var(--cu-head);font-size:1.3em;text-transform:uppercase;line-height:1.1}
${S} .cu-list-title.cu-ar2{font-size:.9em}
/* Columns that wrap: an item that does not fit moves to a column past the box's edge, which is
   clipped, so the screen never shows half an item. */
${S} .cu-list-body{flex:1 1 0;min-height:0;display:flex;flex-direction:column;flex-wrap:wrap;align-content:flex-start;column-gap:1.4em;overflow:hidden}
${S} .cu-list-body>.cu-item{flex:none;width:calc((100% - (var(--cu-cols) - 1) * 1.4em) / var(--cu-cols))}
${listCss(S, 'cu', { name: '1em', price: '1em', desc: '.55em', leader: '.08em dotted color-mix(in srgb,var(--cu-text) 45%,transparent)' })}
${S} .cu-leader-dashed .cu-leader{border-bottom-style:dashed}
${S} .cu-item{margin-bottom:.55em}
${S} .cu-name{font-family:var(--cu-font);font-weight:var(--mb-heading-weight);color:var(--cu-name)}
${S} .cu-upper .cu-name{text-transform:uppercase}
${S} .cu-name2{font-size:.75em;color:var(--cu-name)}
${S} .cu-price{font-family:var(--cu-font);font-weight:var(--mb-heading-weight);color:var(--cu-price)}
${S} .cu-nodesc .cu-desc{display:none}
${S} .cu-list-empty{font-size:.6em;opacity:.6;font-style:italic}
${S} .cu-card{display:flex;gap:.5em;color:var(--cu-text);font-size:34em;overflow:hidden}
${S} .cu-card-v{flex-direction:column;align-items:stretch;gap:1em}
${S} .cu-card-h{flex-direction:row;align-items:center}
/* The photo takes the room the text leaves and is the largest square that fits in it, centred,
   so a circle stays a circle however long the name runs. */
${S} .cu-card-photo{position:relative;flex:1 1 0;min-height:0;min-width:0;display:flex;align-items:center;justify-content:center;container-type:size}
${S} .cu-card-h .cu-card-photo{flex:0 0 45%;align-self:stretch}
${S} .cu-card-photo img{width:100%;height:100%;width:min(100cqw,100cqh);height:min(100cqw,100cqh);object-fit:cover;display:block}
${S} .cu-photo-circle .cu-card-photo img{border-radius:50%}
${S} .cu-photo-rounded .cu-card-photo img{border-radius:.5em}
${S} .cu-card-save{position:absolute;top:0;right:0;background:var(--cu-price);color:var(--mb-bg);font-weight:800;font-size:.55em;
  padding:.3em .6em;border-radius:99em}
${S} .cu-card-text{display:flex;flex-direction:column;gap:.15em;min-width:0}
${S} .cu-card-name{display:block;font-family:var(--cu-font);font-weight:var(--mb-heading-weight);color:var(--cu-name);line-height:1.1}
${S} .cu-card-name.cu-ar2{font-size:.75em}
${S} .cu-card-desc{font-size:.5em;opacity:.8}
${S} .cu-card-badges{display:flex;gap:.3em}
${S} .cu-badge{font-size:.45em;font-weight:800;padding:.15em .5em;border-radius:99em;background:var(--cu-price);color:var(--mb-bg)}
${S} .cu-card-price{font-size:1.1em}
${S} .cu-pricebox{display:inline-flex;align-items:baseline;gap:.4em}
${S} .cu-old{font-size:.6em;opacity:.55}
${S} .cu-card-missing{align-items:center;justify-content:center;font-size:28em;opacity:.5;border:.1em dashed currentColor;color:var(--mb-text)}
`;
